import { Autocomplete, Box, Button, Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, OutlinedInput, Select, TextField } from "@mui/material";
import { eachDeep } from "deepdash-es/standalone";
import { cloneDeep, debounce, isArray, isEqual } from "lodash";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ParentScreenCtx } from "../../../../pages/workbench_build/WorkbenchBuild";
import { treeActions } from "../../../../redux/slices/treeSlice";
import { MoreHorizOutlined } from "@mui/icons-material";
import WorkflowSessionModal1 from "../../WorkflowSessionModal1";
import LightTooltip from "../../../../commonComponents/LightTooltip";
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { genKey, scrollToSelectedItem, validateWorkflowItemName } from "../../../../utils/utilFunctions";
import ErrorModal from "../../Forms/FieldDataValidation/ErrorModal";
import Tree from "rc-tree";
import AdvancedProperties from "../../Forms/Advanced Properties/AdvancedProperties";
import { api } from "../../../../redux/api";
import { ConstantValueControl } from "../../Forms/OutboundFieldFormattingOptions/ConstantValueControl";

export default function TradingPartnerLookupProperties() {
  //default vars
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const parentScreen = useContext(ParentScreenCtx);
  const { rightTree} = useSelector(state => state.treeData[parentScreen]);
  const selectedNode = rightTree.tree.body.selectedNode;
  const { translatedData } = rightTree.tree.body;
  const treeRef  = useRef();
  const parentTreeRef = useRef();
  const treeDataDefault = selectedNode?.TRADING_PARTNER_LOOKUP?.length > 0 ?
    selectedNode.TRADING_PARTNER_LOOKUP.map((obj, index) => {
      return {
        ...obj,
        title: t('MNU_LOOKUP_FLD'),
        key: `${genKey('lookupkeyrand')}-${index}`,
        children: [],
      }
    })
  : [];


  //main save function. clone data, find node in tdata, change obj, dispatch new tdata and selnode
  const submitChange = (key, value, bulk, removeValues) => {
    if (!key && !bulk) return;
    // Clone and deeply iterate through the tree data to update the relevant node.
    const updatedData = eachDeep(cloneDeep(translatedData), (obj) => {
      if (obj?.key !== selectedNode?.key) return;

      if (bulk) {
        bulk.forEach((item) => {
          if (item.value === null) {
            if (obj?.hasOwnProperty(item.key)) delete obj[item.key];
          } else {
            obj[item.key] = item.value;
          }
        });
      } else {
        if (key === "NAME") {
          obj.title = value;
          obj[key] = value;
        } else {
          // Update other properties directly on the object.
          if (value === null) {
            if (obj?.hasOwnProperty(key)) delete obj[key];
          } else {
            obj[key] = value;
          }
        }
      }

      // Dispatch an action to update the selected node in the state.
      dispatch(
        treeActions.setRightTreeSelectedNode({
          screen: parentScreen,
          type: rightTree.currentTab,
          data: obj,
        })
      );

      // If the object has children, return them for further iteration.
      if (isArray(obj?.children)) {
        return obj.children;
      }
    });

    // Dispatch an action to update the translated tree data in the state.
    dispatch(
      treeActions.setNewRightTreeTranslatedData({
        screen: parentScreen,
        type: rightTree.currentTab,
        data: updatedData,
      })
    );
  };

  const debounceChangeHandler = useCallback(debounce(submitChange, 100), [rightTree.tree]);


  //*** "Name" - NAME - text field ***//
  const defaultName = selectedNode?.NAME || "";
  const [name, setName] = useState(defaultName);

  //ID string, displayed on the NAME field
  const wfItemId = selectedNode?.ID;

  // Name validation states
  const [nameErrorModal, setNameErrorModal] = useState(false);
  const [nameErrorMessage, setNameErrorMessage] = useState('');

  // Handle changes to the data name, specific logic for onChange event
  const handleNameChange = (e) => {
    if (e?.target?.value || e?.target?.value === '') {
      setName(e.target.value);
    }
  }

  // Handle changes to the data name, specific logic for onBlur event
  const handleNameBlur = (eVal, e) => {
    const oldName = selectedNode?.NAME || '';
    
    const validationResult = validateWorkflowItemName(
      eVal, 
      oldName, 
      translatedData, 
      selectedNode?.key, 
      'TRADING_PARTNER_LOOKUP'
    );
    
    if (validationResult.isValid) {
      const newName = eVal?.trim() || '';
      setName(newName);
      if (newName !== oldName) {
        // Mark template as changed immediately to ensure flag is set before tab switch
        dispatch(treeActions.setTemplateChanged({ screen: parentScreen, data: true }));
        submitChange('NAME', newName);
        // Wait for debounce delay + buffer before dispatching
        setTimeout(() => {
          dispatch(treeActions.setIsInputFocusedFalse());
        }, 200);
      } else {
        // If no change, dispatch immediately
        setTimeout(() => {
          dispatch(treeActions.setIsInputFocusedFalse());
        }, 100);
      }
    } else {
      if (validationResult.errorMessage) {
        setNameErrorMessage(t(validationResult.errorMessage));
        setNameErrorModal(true);
      } else if (validationResult.shouldRevert) {
        setName(oldName);
      }
      // For error cases, dispatch immediately
      setTimeout(() => {
        dispatch(treeActions.setIsInputFocusedFalse());
      }, 100);
    }
  }

  const handleNameErrorModal = (isOpen) => {
    setNameErrorModal(isOpen);
    if (!isOpen) {
      const oldName = selectedNode?.NAME || '';
      setName(oldName);
    }
  };

  //*** "Do not Log Workflow Item Details" - LOG_WFITEM_DETAILS - checkbox ***//
  const defaultLogWorkflowChecked = selectedNode?.LOG_WFITEM_DETAILS === "FALSE" ? true : false;
  const [logWorkflowChecked, setLogWorkflowChecked] = useState(defaultLogWorkflowChecked);

  const handleLogWorkflowChange = (e) => {
    const checked = e?.target?.checked;
    setLogWorkflowChecked(checked);
    debounceChangeHandler('LOG_WFITEM_DETAILS', checked ? 'FALSE' : 'TRUE');
  }

  //*** "Disable Workflow Item" - ENABLED - checkbox ***//
  const defaultEnabledChecked = selectedNode?.ENABLED === "TRUE" ? false : true;
  const [enabledChecked, setEnabledChecked] = useState(defaultEnabledChecked);

  const handleEnabledChange = (e) => {
    const checked = e?.target?.checked;
    setEnabledChecked(checked);
    debounceChangeHandler('ENABLED', checked ? 'FALSE' : 'TRUE');
  }


  //*** Lookup Field Tree ***//
  const [treeData, setTreeData] = useState(treeDataDefault);
  const [treeSelectedKeys, setTreeSelectedKeys] = useState(treeData?.length > 0 ? [treeData[0].key] : []);
  const [treeSelectedNode, setTreeSelectedNode] = useState(treeData?.length > 0 ? treeData[0] : []);

  const blankEl = <div className='empty-parent-space'></div>;

  const onTreeSelect = (key, node) => {
    setTreeSelectedKeys(key);
    setTreeSelectedNode(node.node);
  }

  const onAddClick = () => {
    const newTreeData = [...treeData];
    const newKey = `${genKey('bghttpkeyrand')}-${newTreeData.length}`;
    //default properties for a new arg
    let newNodeData = {
      FILLCHAR: " ",
      PADDIR: "RIGHT",
      title: t('MNU_LOOKUP_FLD'),
      key: newKey,
      children: [],
    }

    newTreeData.push(newNodeData);
    setTreeData(newTreeData);
    setTreeSelectedKeys([newKey]);
    setTreeSelectedNode(newNodeData);
  }

  const treeIconRenderer = (node) => {
    return <span className="QUERY" style={{marginLeft: "12px"}}></span>
  }

  const deleteTreeNodeByKey = (givenKey) => {
    const newTreeData = treeData.filter((treeNode) => treeNode.key !== givenKey);
    if (newTreeData) {
      setTreeData(newTreeData);
      if (newTreeData.length) {
        setTreeSelectedKeys([newTreeData[0].key]);
        setTreeSelectedNode(newTreeData[0]);
      }
    }
  }

  const treeTitleRender = (node) => {
    const handleDeleteClick = (e) => {
      e.stopPropagation();
      deleteTreeNodeByKey(node.key);

    }
    const iconSize = "18px";
    return (
      <span 
        data-key={`${node.key}`}
        title={node.title}
        className="full-width-tree-node-row " 
      >
        <div
          style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingLeft: "12px"}}
        >
          <span>{node.title}</span>
          <div
            style={{
              
              right: "0px", 
              display: "flex",
              paddingLeft: "8px",
              paddingRight: "12px",
              position: "absolute", 
              alignItems: "center", 
              height: "20px",
              // width: "80px",
            }}
          >
            <LightTooltip
              title={t('DELETE')}
              arrow
              placement="right"
            >
              <span style={{ cursor: 'pointer', height: iconSize, width: iconSize, backgroundSize: iconSize }} className="DELETE_MUI" onClick={handleDeleteClick}></span>
            </LightTooltip>
          </div>
        </div>
      </span>
      
    );
  }

  useEffect(() => {
    const filteredTreeData = treeData.map((node) => {
      let newNode = { ...node };
      delete newNode.key; 
      delete newNode.title; 
      delete newNode.children; 
      return newNode;
    });

    if (
      filteredTreeData && 
      !isEqual(filteredTreeData, selectedNode.TRADING_PARTNER_LOOKUP)
    ) {
      submitChange("TRADING_PARTNER_LOOKUP", filteredTreeData);
    }
  }, [treeData, selectedNode.TRADING_PARTNER_LOOKUP]);

  useEffect(() => {
    scrollToSelectedItem(treeSelectedKeys, parentTreeRef);
  },[treeData, treeSelectedKeys])
  
  return (
    <div>
      {/* "Name" text field + ID number */}
      <FormControl variant='outlined' className='width-100' size='small'>
        <InputLabel
          htmlFor='translated-data-name-input'
          sx={{ padding: "0 6px !important", left: "-5px" }}
        >
          {t('COL_NAME')}
        </InputLabel>
        <OutlinedInput
          id="translated-data-name-input"
          size='small'
          variant='outlined'
          fullWidth
          autoComplete='off'
          value={name}
          endAdornment={<div style={styles.nameIDAdornment}>{`ID: ${wfItemId}`}</div>}
          sx={{ justifyContent: "flex-end" }}
          onChange={(e) => handleNameChange(e)}
          onBlur={(e) => handleNameBlur(e?.target?.value, e)}
          onFocus={() => dispatch(treeActions.setIsInputFocusedTrue())}
        />
      </FormControl>

      {/* "Do not Log Workflow Item Details" checkbox */}
      <FormControl variant='outlined' className='width-100' size='small'>
        <FormControlLabel
          control={
            <Checkbox
              checked={logWorkflowChecked}
              onChange={handleLogWorkflowChange}
              name='logWorkflowChecked'
              color='primary'
            />
          }
          label={t('LBL_LOG_NO_WFITEM_DETAILS')}
        />
      </FormControl>

      {/* "Disable Workflow Item" checkbox */}
      <FormControl variant='outlined' size='small'>
        <FormControlLabel
          control={
            <Checkbox
              checked={enabledChecked}
              onChange={handleEnabledChange}
              name='enabledChecked'
              color='primary'
            />
          }
          label={t('LBL_DISABLE_WFITEM')}
        />
      </FormControl>

      {/* Lookup Field Tree */}
      <div>
        <div style={{...styles.treeHeaderContainer, paddingRight: treeData.length > 4 ? "26px" : "11px"}}>
          <span style={{fontSize: "12px", color: "#666"}}>Edit Lookup Fields:</span>
          <LightTooltip
            title={t('MNU_ADD_ITEM')}
            placement="right"
          >
            <div style={{cursor: 'pointer'}} onClick={onAddClick}>
              <AddOutlinedIcon style={{color: "#666"}} color='#666'/>
            </div>
          </LightTooltip>
        </div>
        <div style={styles.treeContainer} ref={parentTreeRef}>
          <Tree
            ref={treeRef}
            className="tpl-f-tree"
            selectedKeys={treeSelectedKeys}
            onSelect={onTreeSelect}
            treeData={treeData}
            icon={treeIconRenderer}
            titleRender={treeTitleRender}
            showIcon
            switcherIcon={blankEl}
          />
        </div>
      </div>

      <LookupFieldControls 
        treeSelectedNode={treeSelectedNode} 
        setTreeSelectedNode={setTreeSelectedNode} 
        treeData={treeData} 
        setTreeData={setTreeData}
        submitChange={submitChange}
      />

      {/* Error Modal for name validation */}
      <ErrorModal
        errorModal={nameErrorModal}
        setErrorModal={setNameErrorModal}
        errorMessage={nameErrorMessage}
        errorType={t('ERROR')}
        handleErrorModal={handleNameErrorModal}
      />

    </div>
  );
}

const LookupFieldControls = ({
  treeSelectedNode, 
  setTreeSelectedNode, 
  treeData, 
  setTreeData
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const parentScreen = useContext(ParentScreenCtx);
  const { account, user } = useSelector(state => state?.queryParams || {});
  const { rightTree, leftTree } = useSelector(state => state.treeData[parentScreen]);
  const selectedNode = rightTree.tree.body.selectedNode;
 
  const saveToTreeNode = (key, value, bulk) => {
    const newTreeData = treeData.map((node) => {
      if (node.key === treeSelectedNode.key) {
        // Create a new node by cloning the current node
        let newNode = { ...node };
        if (bulk) {
          bulk.forEach((item) => {
            if ((item.value === null || item.value === '') && newNode.hasOwnProperty(item.key)) {
              delete newNode[item.key];
            } else {
              newNode[item.key] = item.value;
            }
          });
        } else {
          if ((value === null || value === '') && newNode.hasOwnProperty(key)) {
            delete newNode[key];
          } else {
            newNode[key] = value;
          }
        }
        setTreeSelectedNode(newNode);
        return newNode;
      }
      // Return unchanged nodes
      return node;
    });
    setTreeData(newTreeData);
  };


  //*** Source Record Name - dropdown - RECORD_TYPE ***/
  const [sourceRecordName, setSourceRecordName] = useState(treeSelectedNode?.RECORD_TYPE || t('SELECT'));
  const [workflowSessionModal, setWorkflowSessionModal] = useState(false);
  const [workflowItem, setWorkflowItem] = useState(treeSelectedNode?.WF_ITEM_LOCATION ? treeSelectedNode?.RECORD_TYPE === "CALL_WORKFLOW_DATA" ? {
    label: `${treeSelectedNode.CALL_WORKFLOW}\\${treeSelectedNode.WF_ITEM_LOCATION}`,
    value: `${treeSelectedNode.CALL_WORKFLOW}\\${treeSelectedNode.WF_ITEM_LOCATION}`,
    WF_ITEM_ID: treeSelectedNode.WF_ITEM_ID
  } : treeSelectedNode?.WF_ITEM_LOCATION  : null);
  const [sourceFieldName, setSourceFieldName] = useState('');
  const [constantValue, setConstantValue] = useState('');
  const [repeatTimes, setRepeatTimes] = useState('1');
  const [accountKeys, setAccountKeys] = useState([]);
  const [tradingPartnerKeys, setTradingPartnerKeys] = useState([]);
  const [pluginOutputValues, setPluginOutputValues] = useState([]);
  const [workflowItems, setWorkflowItems] = useState([]);
  const [sessionWorkflowItems, setSessionWorkflowItems] = useState([]);
  const [sourceFieldNameOptions, setSourceFieldNameOptions] = useState([]);
  const [workflowSourceFields, setWorkflowSourceFields] = useState([]);
  const [enableOutboundConditionControl, setEnableOutboundConditionControl] = useState(false);
  const [outboundCondition, setOutboundCondition] = useState(t('SELECT'));
  const [outboundConditionOptions, setOutboundConditionOptions] = useState([]);
  const [trimDataChecked, setTrimDataChecked] = useState(treeSelectedNode?.TRIMDATA === "TRUE" || false);
  const [caseConversion, setCaseConversion] = useState(treeSelectedNode?.CONVERT_CASE || 'DO_NOT_CONVERT');
  const [paddingDirection, setPaddingDirection] = useState(treeSelectedNode?.PADDIR || 'RIGHT');
  const [fillCharacter, setFillCharacter] = useState(treeSelectedNode?.FILLCHAR || " ");

  const getWorkflowItemOptions = useCallback(() => {
    const items = ["VARIABLE_ITEM", "GET_DATA", "TRANSLATE", "SEND_DATA", "LOOP_ITEM", "CONDITION_IF", "CALL_PLUGIN", "CALL_WORKFLOW", "TRADING_PARTNER_LOOKUP", "ACTION", "ERROR_HANDLER", "TRACKING_REPORT_ITEM"];
    let options = [];
    let foundSelectedNode = false;

    const traverseTree = (node) => {
      if (node?.key === selectedNode?.key) {
        // Include the selected node in options if it's a valid workflow item
        if (node?.key && node?.ICON_DISPLAY && items.includes(node?.ICON_DISPLAY)) {
          const alreadyExist = options.filter((ele) => (ele.value === node?.NAME && ele?.isVariable && node?.ICON_DISPLAY === "VARIABLE_ITEM")).length >= 1;
          if (!alreadyExist) {
            options.push({
              label: node?.NAME,
              value: node?.NAME,
              isTranslatedData: node?.ICON_DISPLAY === "TRANSLATE",
              WF_ITEM_ID: node?.ID,
              isLoop: node?.ICON_DISPLAY === "LOOP_ITEM",
              isVariable: node?.ICON_DISPLAY === "VARIABLE_ITEM",
              isCONDITION_IF: node?.ICON_DISPLAY === "CONDITION_IF"
            });
          }
        }
        foundSelectedNode = true;
        return;
      }

      if (!foundSelectedNode && node?.key && node?.ICON_DISPLAY && items.includes(node?.ICON_DISPLAY)) {
        const alreadyExist = options.filter((ele) => (ele.value === node?.NAME && ele?.isVariable && node?.ICON_DISPLAY === "VARIABLE_ITEM")).length >= 1;
        if (!alreadyExist) {
          options.push({
            label: node?.NAME,
            value: node?.NAME,
            isTranslatedData: node?.ICON_DISPLAY === "TRANSLATE",
            WF_ITEM_ID: node?.ID,
            isLoop: node?.ICON_DISPLAY === "LOOP_ITEM",
            isVariable: node?.ICON_DISPLAY === "VARIABLE_ITEM",
            isCONDITION_IF: node?.ICON_DISPLAY === "CONDITION_IF"
          });
        }
      }

      if (!foundSelectedNode && Array.isArray(node?.children)) {
        node.children.forEach(child => traverseTree(child));
      }
    };

    // Start traversal from the root
    if (Array.isArray(rightTree.tree.body.translatedData)) {
      rightTree.tree.body.translatedData.forEach(node => traverseTree(node));
    }

    options = options.sort((a, b) => {
      const labelA = a.label ? a.label.toLowerCase() : '';
      const labelB = b.label ? b.label.toLowerCase() : '';
      if (labelA < labelB) return -1;
      if (labelA > labelB) return 1;
      return 0;
    });
    
    return [{ label: t("SELECT"), value: t("SELECT") }, ...options];
  }, [rightTree.tree.body.translatedData, t]);


  // Effect to handle session data and outbound conditions
  useEffect(() => {
    const handleSessionData = async () => {
      if (sourceRecordName !== 'SESSION_DATA' || !sessionWorkflowItems.length) return;

      try {
        // Handle session data fields
        const selectedItem = sessionWorkflowItems.find(item => item.value === workflowItem);
        if (!selectedItem || selectedItem.value === t('SELECT')) {
          setSourceFieldNameOptions([{ value: t('SELECT'), label: t('SELECT') }]);
          setOutboundConditionOptions([]);
          setEnableOutboundConditionControl(false);
          return;
        }

        // Handle special workflow item types
        if (selectedItem.isLoop) {
          setSourceFieldNameOptions([
            // { value: t('SELECT'), label: t('SELECT') },
            { value: 'LOOP_COUNT', label: t('LOOP_COUNT') }
          ]);
          setEnableOutboundConditionControl(false);
        } else if (selectedItem.isVariable) {
          setSourceFieldNameOptions([
            // { value: t('SELECT'), label: t('SELECT') },
            { value: 'VALUE', label: t('VALUE') }
          ]);
          setEnableOutboundConditionControl(false);
        } else if (selectedItem.isCONDITION_IF) {
          setSourceFieldNameOptions([]);
          setEnableOutboundConditionControl(false);
        } else {
          // Determine the outbound condition to use
          let outboundConditionToUse = "";
          if (outboundCondition && outboundCondition !== t('SELECT')) {
            outboundConditionToUse = outboundCondition;
          }

          // Fetch session data fields
          const response = await dispatch(api.endpoints.getSessionData.initiate({
            account,
            user,
            workflow_group_name: leftTree.tree.body.selectedNode?.workflowGroup,
            workflow_name: leftTree.tree.body.selectedNode?.title,
            source_record_name: sourceRecordName.toLowerCase(),
            wf_item: workflowItem,
            outbound_condition_name: "",
            variable_name: ""
          }, { forceRefetch: true }));

          if (response?.data?.current_state === "SUCCESS") {
            let data = cloneDeep(response?.data?.data || []);
            if (sourceFieldName && sourceFieldName !== t('SELECT') && !data?.includes(sourceFieldName)) {
              data.unshift(sourceFieldName);
            }
            if(selectedItem?.isTranslatedData){
              setOutboundConditionOptions(response?.data?.data || []);
              setEnableOutboundConditionControl(true);
              
              // If we have an outbound condition, fetch the filtered fields
              if (outboundConditionToUse) {
                const outboundResponse = await dispatch(api.endpoints.getSessionData.initiate({
                  account,
                  user,
                  workflow_group_name: leftTree.tree.body.selectedNode?.workflowGroup,
                  workflow_name: leftTree.tree.body.selectedNode?.title,
                  source_record_name: sourceRecordName.toLowerCase(),
                  wf_item: workflowItem,
                  outbound_condition_name: outboundConditionToUse,
                  variable_name: ""
                }, { forceRefetch: true }));
                
                if (outboundResponse?.data?.current_state === "SUCCESS") {
                  setSourceFieldNameOptions([
                    ...outboundResponse?.data?.data.map(opt => ({
                      value: opt,
                      label: t(opt)
                    }))
                  ]);
                }
              }
              return;
            } else {
              setOutboundConditionOptions([]);
              setEnableOutboundConditionControl(false);
            }
            
            // data = data.sort((a, b) => {
            //   const labelA = a.label ? a.label.toLowerCase() : '';
            //   const labelB = b.label ? b.label.toLowerCase() : '';
            //   if (labelA < labelB) return -1; 
            //   if (labelA > labelB) return 1;
            //   return 0;
            // });
            setSourceFieldNameOptions([
              
              ...data?.map(opt => ({
                value: opt,
                label: t(opt)
              }))
            ]);
          } else {
            setSourceFieldNameOptions([{ value: t('SELECT'), label: t('SELECT') }]);
            setOutboundConditionOptions([]);
            setEnableOutboundConditionControl(false);
          }
        }

      } catch (error) {
        console.error('Error handling session data:', error);
        setSourceFieldNameOptions([{ value: t('SELECT'), label: t('SELECT') }]);
        setOutboundConditionOptions([]);
        setEnableOutboundConditionControl(false);
      }
    };

    handleSessionData();
  }, [workflowItem, sessionWorkflowItems, outboundCondition]);

  // Update initial state setup
  useEffect(() => {
    if (!treeSelectedNode) {
      // Reset all states if no node is selected
      setSourceRecordName(t('SELECT'));
      setSourceFieldName(t('SELECT'));
      setWorkflowItem(null);
      setWorkflowItems([]);
      setWorkflowSourceFields([]);
      setTrimDataChecked(false);
      setCaseConversion('DO_NOT_CONVERT');
      setPaddingDirection('RIGHT');
      setFillCharacter(" ");
      setOutboundCondition(t('SELECT'));
      setOutboundConditionOptions([]);
      setEnableOutboundConditionControl(false);
      setSessionWorkflowItems([]);
      return;
    }

    // Set initial values from tree node
    setSourceRecordName(treeSelectedNode.RECORD_TYPE || t('SELECT'));
    setTrimDataChecked(treeSelectedNode.TRIMDATA === "TRUE" || false);
    setCaseConversion(treeSelectedNode.CONVERT_CASE || 'DO_NOT_CONVERT');
    setPaddingDirection(treeSelectedNode.PADDIR || 'RIGHT');
    setFillCharacter(treeSelectedNode.FILLCHAR || " ");
    if(!treeSelectedNode?.OUTBOUND) {
      setEnableOutboundConditionControl(false);
      setOutboundCondition(t('SELECT'));
    }
    // Handle record type specific settings
    if (treeSelectedNode.RECORD_TYPE === "CONSTANT") {
      setConstantValue(treeSelectedNode.VALUE || '');
      setRepeatTimes(treeSelectedNode.REPEAT || '1');
      setEnableOutboundConditionControl(false);
    } 
    else if (treeSelectedNode.RECORD_TYPE === "SESSION_DATA") {
      setWorkflowItem(treeSelectedNode?.WF_ITEM_LOCATION || t('SELECT'));
      setSourceFieldName(treeSelectedNode?.MAPNAME || t('SELECT'));
      
      // Initialize outbound condition state
      if (treeSelectedNode?.OUTBOUND) {
        setEnableOutboundConditionControl(true);
        setOutboundConditionOptions([treeSelectedNode.OUTBOUND]);
        setOutboundCondition(treeSelectedNode.OUTBOUND);
      } else {
        setOutboundCondition(t('SELECT'));
      }
    }
    else if (treeSelectedNode.RECORD_TYPE === "CALL_WORKFLOW_DATA") {
      const workflowValue = {
        label: `${treeSelectedNode.CALL_WORKFLOW}\\${treeSelectedNode.WF_ITEM_LOCATION}`,
        value: `${treeSelectedNode.CALL_WORKFLOW}\\${treeSelectedNode.WF_ITEM_LOCATION}`,
        WF_ITEM_ID: treeSelectedNode.WF_ITEM_ID
      };
      setWorkflowItem(treeSelectedNode?.WF_ITEM_LOCATION ? workflowValue : '');
      setSourceFieldName(treeSelectedNode?.MAPNAME || t('SELECT'));
      if (treeSelectedNode?.OUTBOUND) {
        dispatch(api.endpoints.getSessionData.initiate({
          account: treeSelectedNode?.CALL_WORKFLOW_ACCOUNT || account,
          user,
          workflow_group_name: treeSelectedNode?.CALL_WORKFLOW_GROUP,
          workflow_name: treeSelectedNode.CALL_WORKFLOW,
          source_record_name: "CALL_WORKFLOW_DATA".toLowerCase(),
          wf_item: workflowValue?.value?.split("\\")?.[1] || "",
          outbound_condition_name: '',
          variable_name: ""
        }, { forceRefetch: true }))
          .then(res => {
            if (res.data.current_state === "SUCCESS") {
              const data = res?.data?.data || [];
              setOutboundConditionOptions(data);
            }
          });
        setEnableOutboundConditionControl(true);
        setOutboundConditionOptions([treeSelectedNode.OUTBOUND]);
        setOutboundCondition(treeSelectedNode.OUTBOUND);
        
        // Fetch the source field options with the outbound condition
        if (treeSelectedNode.CALL_WORKFLOW && treeSelectedNode.WF_ITEM_LOCATION) {
          dispatch(api.endpoints.getSessionData.initiate({
            account: treeSelectedNode?.CALL_WORKFLOW_ACCOUNT || account,
            user,
            workflow_group_name: treeSelectedNode?.CALL_WORKFLOW_GROUP,
            workflow_name: treeSelectedNode.CALL_WORKFLOW,
            source_record_name: "CALL_WORKFLOW_DATA".toLowerCase(),
            wf_item: workflowValue?.value?.split("\\")?.[1] || "",
            outbound_condition_name: treeSelectedNode.OUTBOUND,
            variable_name: ""
          }, { forceRefetch: true }))
            .then(res => {
              if (res.data.current_state === "SUCCESS") {
                const data = res?.data?.data || [];
                setWorkflowSourceFields(data)
                // setSourceFieldNameOptions([...data.map(opt => ({
                //   value: opt,
                //   label: t(opt)
                // }))]);
              }
            });
        }
      } else {
        setOutboundCondition(t('SELECT'));
      }
    }
    else {
      // For all other record types
      setSourceFieldName(treeSelectedNode?.MAPNAME || t('SELECT'));
    }
  }, [treeSelectedNode?.key]);
 // Fetch required data based on source record type
 useEffect(() => {
  const fetchKeys = async () => {
    if(sourceRecordName === "ACCOUNT_KEYS") {
      const response = await dispatch(api.endpoints.getAccountData.initiate({account: account}));
      if(response?.data?.current_state === "SUCCESS") {
        setAccountKeys(response?.data?.data?.account_data_keys.map(o => Object?.keys(o)?.[0]));
      }
    } else if (sourceRecordName === "TRADING_PARTNER_KEYS") {
      const response = await dispatch(api.endpoints.getTradingPartnersKeys.initiate({account: account}));
      if(response?.data?.current_state === "SUCCESS") {
        setTradingPartnerKeys(response?.data?.data?.trading_partner_keys);
      }
    } else if(sourceRecordName === 'PLUGIN') {
      // Gather plugin outputs recursively until we hit our node
      const gatherPluginOutputs = (nodes, outputs = []) => {
        for (const node of nodes) {
          if (node.key === selectedNode.key) {
            return outputs;
          }
          if (node.ACTION === 'CALL_PLUGIN' && node.OUT_ARG) {
            outputs.push(...node.OUT_ARG.map(arg => arg.MAPNAME).filter(Boolean));
          }
          if (node.children && node.children.length > 0) {
            gatherPluginOutputs(node.children, outputs);
          }
        }
        return outputs;
      };
      const pluginOutputs = gatherPluginOutputs(rightTree.tree.body.translatedData);
      setPluginOutputValues([ t('SELECT'),...pluginOutputs]);
    } else if (sourceRecordName === 'SESSION_DATA') {
      const options = getWorkflowItemOptions();
      setSessionWorkflowItems(options);
    }
  };
  fetchKeys();
}, [sourceRecordName]);

  const sourceRecordNameChange = (e) => {
    if (e?.target?.value) {
      const newValue = e.target.value;
      setSourceRecordName(newValue);
      setSourceFieldName(t('SELECT'));
      setOutboundCondition(t('SELECT'));
      setOutboundConditionOptions([]);
      setEnableOutboundConditionControl(false);
      setConstantValue("");
      setRepeatTimes('1');
      setWorkflowItem(null);
      setWorkflowItems([]);
      setWorkflowSourceFields([]);
      setSourceFieldNameOptions([]);
      if (newValue === "SESSION_DATA") {
        setSessionWorkflowItems(getWorkflowItemOptions());
      } else {
        setSessionWorkflowItems([]);
      }

      if (newValue === t('SELECT')) {
        saveToTreeNode(null, null, [
          { key: "RECORD_TYPE", value: null },
          { key: "MAPNAME", value: null },
          { key: "VALUE", value: null },
          { key: "FIELDTYPE", value: null },
          { key: "REPEAT", value: null },
          { key: "CALL_WORKFLOW", value: null },
          { key: "CALL_WORKFLOW_ACCOUNT", value: null },
          { key: "CALL_WORKFLOW_GROUP", value: null },
          { key: "WF_ITEM_LOCATION", value: null },
          { key: "WF_ITEM_ID", value: null },
          { key: "OUTBOUND", value: null }
        ]);
      } else {
        if(newValue === "CONSTANT") {
          saveToTreeNode(null, null, [
            { key: "CONVERT_CASE", value: null },
            { key: "TRIMDATA", value: null },
          ]);
        } 
          saveToTreeNode(null, null, [
          { key: "RECORD_TYPE", value: newValue },
          { key: "MAPNAME", value: null },
          { key: "VALUE", value: null },
          { key: "FIELDTYPE", value: newValue === "CONSTANT" ? "CONSTANT" : null },
          { key: "REPEAT", value: null },
          { key: "CALL_WORKFLOW", value: null },
          { key: "CALL_WORKFLOW_ACCOUNT", value: null },
          { key: "CALL_WORKFLOW_GROUP", value: null },
          { key: "WF_ITEM_LOCATION", value: null },
          { key: "WF_ITEM_ID", value: null },
          { key: "OUTBOUND", value: null }
          ]);
        
      }
    }
  };
 
  // Define field options based on record type
  const getFieldOptions = (recordType) => {
    switch(recordType) {
      case 'CALL_WORKFLOW_DATA':
        return workflowSourceFields;
      case 'ACCOUNT_KEYS':
        return accountKeys;
      case 'TRADING_PARTNER_KEYS':
        return tradingPartnerKeys;
      case 'SESSION_DATA':
        if (!sessionWorkflowItems.length) return [];
        let item = sessionWorkflowItems.find(item => item.value === sourceFieldName);
        if (!item) return [];
        if (item.isLoop) {
          return ['LOOP_COUNT'];
        } else if (item.isVariable) {
          return ['VALUE'];
        } else if (item.isCONDITION_IF) {
          return [];
        }
        return sourceFieldNameOptions;
      case 'ELEM_VAL_ERR':
        return [t('ERROR_STRING'), t('MAP_ERROR_EXIST')];
      case 'FNCT_GRP_VAL_ERR':
        return [t('ERROR_STRING'), t('MAP_ERROR_EXIST'), t('FNCT_GRP_ACKT_CODE')];
      case 'SEG_VAL_ERR':
        return [t('ERROR_STRING'), t('MAP_ERROR_EXIST')];
      case 'TRN_SET_VAL_ERR':
        return [t('ERROR_STRING'), t('MAP_ERROR_EXIST'), t('TRAN_SET_ACK_CODE')];
      case 'PLUGIN':
        return pluginOutputValues;
      default:
        return [];
    }
  };

 
  const handleSourceFieldChange = (e, newValue) => {
    const value = newValue?.value || t('SELECT');
    setSourceFieldName(value);
    if (!value || value === t('SELECT')) {
      saveToTreeNode("MAPNAME", null);
    } else {
      saveToTreeNode("MAPNAME", value);
    }
  };

  const handleOutboundConditionChange = (newValue) => {
    setOutboundCondition(newValue);
    saveToTreeNode("OUTBOUND", newValue === t('SELECT') ? null : newValue);
    if(newValue !== t('SELECT')) {
      dispatch(api.endpoints.getSessionData.initiate({
        account: account,
        user: user,
        workflow_group_name: leftTree.tree.body.selectedNode?.workflowGroup,
        workflow_name: leftTree.tree.body.selectedNode?.title,
        source_record_name: sourceRecordName.toLowerCase(),
        wf_item: workflowItem|| "",
        outbound_condition_name: newValue,
        variable_name: ""
      }, { forceRefetch: true }))
      .then(res => {
        if (res.data.current_state === "SUCCESS") {
          setSourceFieldNameOptions([...res?.data?.data.map(o => ({
            value:o,
            label:t(o)
          }))
        ]);
        } else {
          setSourceFieldNameOptions([]);
        }
      })
    }
  };
  //*** Case Conversion - dropdown - CONVERT_CASE ***/
  const handleCaseChange = (e) => {
    if (e?.target?.value) {
      setCaseConversion(e?.target?.value);
      const newValue = e.target.value === 'DO_NOT_CONVERT' ? null : e.target.value;
      if (newValue !== treeSelectedNode?.CONVERT_CASE) {
        saveToTreeNode("CONVERT_CASE", newValue);
      }
    }
  };

  //*** Padding Direction - dropdown - PADDIR ***/
  const handlePaddingDirectionChange = (e) => {
    if (e?.target?.value && e.target.value !== paddingDirection) {
      setPaddingDirection(e.target.value);
      saveToTreeNode("PADDIR", e.target.value);
    }
  };


  //*** Trim Data - checkbox - TRIMDATA ***/
  const handleTrimDataChange = (e) => {
    const checked = e?.target?.checked;
    setTrimDataChecked(checked);
    if (checked !== (treeSelectedNode?.TRIMDATA === "TRUE")) {
      saveToTreeNode("TRIMDATA", checked ? "TRUE" : null);
    }
  };

  const sourceRecordNameOptions = [
    <MenuItem key={`SELECT-0`} value={t('SELECT')} >{t('SELECT')}</MenuItem>,
    <MenuItem key={`ACCOUNT_KEYS-1`} value={"ACCOUNT_KEYS"}>{t('ACCOUNT_KEYS')}</MenuItem>,
    <MenuItem key={`CALL_WORKFLOW_DATA-2`} value={"CALL_WORKFLOW_DATA"}>{t('CALL_WORKFLOW_DATA')}</MenuItem>,
    <MenuItem key={`CONSTANT-3`} value={"CONSTANT"}>{t('CONSTANT')}</MenuItem>,
    <MenuItem key={`ELEM_VAL_ERR-4`} value={"ELEM_VAL_ERR"}>{t('ELEM_VAL_ERR')}</MenuItem>,
    <MenuItem key={`FNCT_GRP_VAL_ERR-5`} value={"FNCT_GRP_VAL_ERR"}>{t('FNCT_GRP_VAL_ERR')}</MenuItem>,
    <MenuItem key={`PLUGIN-6`} value={"PLUGIN"}>{t('PLUGIN')}</MenuItem>,
    <MenuItem key={`SEG_VAL_ERR-7`} value={"SEG_VAL_ERR"}>{t('SEG_VAL_ERR')}</MenuItem>,
    <MenuItem key={`SESSION_DATA-8`} value={"SESSION_DATA"}>{t('SESSION_DATA')}</MenuItem>,
    <MenuItem key={`TRADING_PARTNER_KEYS-9`} value={"TRADING_PARTNER_KEYS"}>{t('TRADING_PARTNER_KEYS')}</MenuItem>,
    <MenuItem key={`TRN_SET_VAL_ERR-10`} value={"TRN_SET_VAL_ERR"}>{t('TRN_SET_VAL_ERR')}</MenuItem>
  ];

  // Handle workflow session modal value selection
  const handleWorkflowSessionModalValue = useCallback(([value]) => {
    if (value) {
      const workflowValue = {
        label: `${value?.template}\\${value?.parent}`,
        value: `${value?.template}\\${value?.parent}`,
        WF_ITEM_ID: value?.WF_ITEM_ID,
      };
      setWorkflowItem(workflowValue);

      // Set source fields from the workflow data
      if (value?.MAPNAME) {
        setSourceFieldName(value.MAPNAME);
      } else {
        setWorkflowSourceFields([]);
        setSourceFieldName(t("SELECT"));
      }

      if(value?.condition) {
        dispatch(api.endpoints.getSessionData.initiate({
          account: sourceRecordName === "CALL_WORKFLOW_DATA" ? treeSelectedNode?.CALL_WORKFLOW_ACCOUNT : account,
          user,
          workflow_group_name: value?.workflowGroup,
          workflow_name: value?.template,
          source_record_name: "CALL_WORKFLOW_DATA".toLowerCase(),
          wf_item: workflowValue?.value?.split("\\")?.[1] || "",
          outbound_condition_name: '',
          variable_name: ""
        }, { forceRefetch: true }))
          .then(res => {
            if (res.data.current_state === "SUCCESS") {
              const data = res?.data?.data || [];
              setOutboundConditionOptions(data);
          } else {
              setOutboundConditionOptions([]);
            }
          });
       
        setOutboundConditionOptions([value?.condition]);
        setOutboundCondition(value.condition);
        setEnableOutboundConditionControl(true);

        dispatch(api.endpoints.getSessionData.initiate({
          account: sourceRecordName === "CALL_WORKFLOW_DATA" ? treeSelectedNode?.CALL_WORKFLOW_ACCOUNT : account,
          user,
          workflow_group_name: value?.workflowGroup,
          workflow_name: value?.template,
          source_record_name: "CALL_WORKFLOW_DATA".toLowerCase(),
          wf_item: workflowValue?.value?.split("\\")?.[1] || "",
          outbound_condition_name: value?.condition,
          variable_name: ""
        }, { forceRefetch: true }))
          .then(res => {
            if (res.data.current_state === "SUCCESS") {
              const data = res?.data?.data || [];
              setWorkflowSourceFields(data);
          } else {
              setSourceFieldNameOptions([]);
            }
          });
      } else {
        dispatch(api.endpoints.getSessionData.initiate({
          account: sourceRecordName === "CALL_WORKFLOW_DATA" ? treeSelectedNode?.CALL_WORKFLOW_ACCOUNT : account,
          user,
          workflow_group_name: value?.workflowGroup,
          workflow_name: value?.template,
          source_record_name: "CALL_WORKFLOW_DATA".toLowerCase(),
          wf_item: workflowValue?.value?.split("\\")?.[1] || "",
          outbound_condition_name: "",
          variable_name: ""
        }, { forceRefetch: true }))
          .then(res => {
            if (res.data.current_state === "SUCCESS") {
              const data = res?.data?.data || [];
              setSourceFieldNameOptions([...data.map(opt => ({
                value: opt,
                label: t(opt)
              }))]);
            } else {
              setSourceFieldNameOptions([]);
            }
          });
      }

      // Save to tree node with all data while preserving existing properties
      saveToTreeNode(null, null, [
        { key: "RECORD_TYPE", value: "CALL_WORKFLOW_DATA" },
        { key: "WF_ITEM_LOCATION", value: value?.parent },
        { key: "CALL_WORKFLOW", value: value?.template },
        { key: "CALL_WORKFLOW_ACCOUNT", value: value?.account },
        { key: "CALL_WORKFLOW_GROUP", value: value?.workflowGroup },
        { key: "WF_ITEM_ID", value: value?.WF_ITEM_ID ? `${value.WF_ITEM_ID}` : null },
        { key: "MAPNAME", value: value?.MAPNAME || null },
        { key: "OUTBOUND", value: value?.condition || null }
      ]);
    }
    setWorkflowSessionModal(false);
  }, [t, sourceRecordName, treeSelectedNode, account, user]);

  // Update outbound condition Select component render
  return (
    <>
      <FormControl
        variant="outlined"
        className="width-100"
        size="small"
        style={{ marginTop: "15px" }}>
        <InputLabel>{t("LBL_SOURCE_RECORD_NAME")}</InputLabel>
        <Select
          variant="outlined"
          size="small"
          className={"width-100"}
          value={sourceRecordName}
          onChange={sourceRecordNameChange}>
          {sourceRecordNameOptions}
        </Select>
      </FormControl>

      {sourceRecordName === "CALL_WORKFLOW_DATA" && (
        <Box sx={{ display: "flex", gap: 1, marginTop: "15px" }}>
          <Autocomplete
            fullWidth
            freeSolo
            size="small"
            options={workflowItems.map((item) => ({
              label: item?.label || `${item?.template}\\${item?.parent}`,
              value: item?.value || `${item?.template}\\${item?.parent}`,
            }))}
            value={workflowItem}
            onChange={(e, value) => {
              setWorkflowItem(value);
              setSourceFieldName("");
              if (value) {
                saveToTreeNode(null, null, [
                  {
                    key: "WF_ITEM_LOCATION",
                    value: value?.value?.split("\\")?.[1] || null,
                  },
                  {
                    key: "CALL_WORKFLOW",
                    value: value?.value?.split("\\")?.[0] || null,
                  },
                  {
                    key: "WF_ITEM_ID",
                    value: value?.WF_ITEM_ID ? `${value.WF_ITEM_ID}` : null,
                  },
                  { key: "MAPNAME", value: null },
                  { key: "OUTBOUND", value: null }
                ]);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("LBL_WORKFLOW_ITEM")}
                variant="outlined"
                size="small"
              />
            )}
          />
          <Button
            variant="outlined"
            onClick={() => setWorkflowSessionModal(true)}>
            <MoreHorizOutlined />
          </Button>
        </Box>
      )}

      {sourceRecordName === "CONSTANT" && (
        <>
          <FormControl
            variant="outlined"
            className="width-100"
            size="small"
            style={{ marginTop: "15px" }}>
            <ConstantValueControl
              constantValue={constantValue}
              setConstantValue={(value) => {
                const newValue = value || "";
                setConstantValue(newValue);
                if (!value) {
                  saveToTreeNode(null, null, [
                    { key: "VALUE", value: null },
                    { key: "FIELDTYPE", value: null },
                  ]);
                } else {
                  let finalValue = newValue;
                  saveToTreeNode(null, null, [
                    { key: "VALUE", value: finalValue },
                    { key: "FIELDTYPE", value: "CONSTANT" },
                  ]);
                }
              }}
              constantValueOptions={[t("CRLF"), t("RECORD_DELIMITER")]}
            />
          </FormControl>
          <FormControl
            variant="outlined"
            className="width-100"
            size="small"
            style={{ marginTop: "15px", marginBottom: "15px" }}>
            <TextField
              label={t("LBL_REPEAT_#_TIMES")}
              variant="outlined"
              size="small"
              type="number"
              value={repeatTimes}
              inputProps={{
                min: "1",
                max: "999999999",
              }}
              onChange={(e) => {
                const value = e.target.value;
                // Only allow numbers and limit to 9 digits
                const newValue = value.substring(0, 9).replace(/\D/g, "");
                setRepeatTimes(newValue);
                if (newValue) {
                  saveToTreeNode("REPEAT", newValue);
                }
              }}
              onBlur={(e) => {
                // If empty or 0, set to 1
                if (!repeatTimes || repeatTimes === "0") {
                  // Mark template as changed immediately to ensure flag is set before tab switch
                  dispatch(treeActions.setTemplateChanged({ screen: parentScreen, data: true }));
                  setRepeatTimes("1");
                  saveToTreeNode("REPEAT", "1");
                }
              }}
            />
          </FormControl>
        </>
      )}
      {/* Workflow Item Selection for SESSION_DATA */}
      {sourceRecordName === "SESSION_DATA" && (
        <Box sx={{ display: "flex", gap: 1, marginTop: "15px" }}>
          <Autocomplete
            options={sessionWorkflowItems}
            getOptionLabel={(option) => option?.label || ""}
            isOptionEqualToValue={(option, value) =>
              option?.value === value?.value
            }
            value={
              sessionWorkflowItems.find(
                (item) => item.value === workflowItem
              ) || {label: t('SELECT'), value: t('SELECT')}
            }
            onChange={(e, val) => {
              if (!val || val?.value === t("SELECT")) {
                setWorkflowItem("");
                setSourceFieldName(t('SELECT'));
                saveToTreeNode(null, null, [
                  { key: "WF_ITEM_LOCATION", value: null },
                  { key: "WF_ITEM_ID", value: null },
                  { key: "MAPNAME", value: null },
                  { key: "OUTBOUND", value: null }
                ]);
              } else {
                setWorkflowItem(val.value);
                setSourceFieldName(t('SELECT'));
                saveToTreeNode(null, null, [
                  { key: "WF_ITEM_LOCATION", value: val.value },
                  {
                    key: "WF_ITEM_ID",
                    value: val.WF_ITEM_ID ? `${val.WF_ITEM_ID}` : null,
                  },
                  { key: "MAPNAME", value: null },
                  { key: "OUTBOUND", value: null }
                ]);
              }
            }}
            fullWidth
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("LBL_WORKFLOW_ITEM")}
                variant="outlined"
                size="small"
              />
            )}
          />
        </Box>
      )}
      {enableOutboundConditionControl && (
        <FormControl
          variant="outlined"
          className="width-100"
          size="small"
          style={{ marginTop: "15px" }}>
          <InputLabel>{t("LBL_OUT_COND")}</InputLabel>
          <Select
            variant="outlined"
            size="small"
            className={"width-100"}
            value={outboundCondition}
            onChange={(e) => {
              const newValue = e.target.value;
              handleOutboundConditionChange(newValue);
            }}
            label={t("LBL_OUT_COND")}>
            <MenuItem value={t('SELECT')}>{t('SELECT')}</MenuItem>
            {outboundConditionOptions.map((item, index) => (
              <MenuItem value={item} key={`outbound-condition-${index}`}>
                {t(item)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {sourceRecordName !== t("SELECT") && sourceRecordName !== "CONSTANT" && (
        <FormControl
          variant="outlined"
          className="width-100"
          size="small"
          style={{ marginTop: "15px" }}>
          {sourceRecordName === "SESSION_DATA" ? (
            workflowItem && workflowItem !== t('SELECT') ? (
              <Autocomplete
                options={sourceFieldNameOptions}
                getOptionLabel={(option) => option.label || ""}
                isOptionEqualToValue={(option, value) =>
                  option?.value === value?.value
                }
                value={sourceFieldNameOptions?.find(opt => opt.value === sourceFieldName) || { value: t('SELECT'), label: t('SELECT') }}
                onChange={(e, newValue) => {
                  const value = newValue?.value || t('SELECT');
                  setSourceFieldName(value);
                  saveToTreeNode("MAPNAME", value === t('SELECT') ? null : value);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("LBL_SOURCE_FIELD_NAME")}
                    variant="outlined"
                    size="small"
                  />
                )}
              />
            ) : null
          ) : sourceRecordName === "CALL_WORKFLOW_DATA" ? (
            // Only show source field name if we have MAPNAME from workflow session modal
            treeSelectedNode?.MAPNAME ? (
              <Autocomplete
                fullWidth
                freeSolo
                options={workflowSourceFields.map(opt => ({
                  value: opt,
                  label: t(opt)
                }))}
                getOptionLabel={(option) => option.label || ""}
                isOptionEqualToValue={(option, value) =>
                  option?.value === value?.value
                }
                value={{ value: sourceFieldName, label: t(sourceFieldName) }}
                onChange={handleSourceFieldChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("LBL_SOURCE_FIELD_NAME")}
                    variant="outlined"
                    size="small"
                  />
                )}
              />
            ) : null
          ) : (
            <Autocomplete
              fullWidth
              freeSolo
              options={getFieldOptions(sourceRecordName).map(opt => ({
                value: opt,
                label: sourceRecordName === "PLUGIN" ? opt : t(opt)
              }))}
              getOptionLabel={(option) => option.label || ""}
              isOptionEqualToValue={(option, value) =>
                option?.value === value?.value
              }
              value={{ value: sourceFieldName, label: sourceRecordName === "PLUGIN" ? sourceFieldName : t(sourceFieldName) }}
              onChange={handleSourceFieldChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("LBL_SOURCE_FIELD_NAME")}
                  variant="outlined"
                  size="small"
                />
              )}
            />
          )}
        </FormControl>
      )}

      {sourceRecordName !== "CONSTANT" && (
        <FormControl
          variant="outlined"
          className="width-100"
          size="small"
          style={{ marginTop: "15px" }}>
          <InputLabel>{t("LBL_CONVERT_CASE")}</InputLabel>
          <Select
            variant="outlined"
            size="small"
            className="width-100"
            value={caseConversion}
            onChange={handleCaseChange}
            label={t("LBL_CONVERT_CASE")}>
            <MenuItem value="DO_NOT_CONVERT">{t("DO_NOT_CONVERT")}</MenuItem>
            <MenuItem value="UPPER_CASE">{t("UPPER_CASE")}</MenuItem>
            <MenuItem value="LOWER_CASE">{t("LOWER_CASE")}</MenuItem>
            <MenuItem value="PROPER_CASE">{t("PROPER_CASE")}</MenuItem>
          </Select>
        </FormControl>
      )}

      {sourceRecordName !== "CONSTANT" && (
        <FormControl
          variant="outlined"
          className="width-100"
          size="small"
          style={{ marginTop: "15px" }}>
          <InputLabel>{t("LBL_PADDING_DIRECTION")}</InputLabel>
          <Select
            variant="outlined"
            size="small"
            className="width-100"
            value={paddingDirection}
            onChange={handlePaddingDirectionChange}
            label={t("LBL_PADDING_DIRECTION")}>
            <MenuItem value="LEFT">{t("LEFT")}</MenuItem>
            <MenuItem value="RIGHT">{t("RIGHT")}</MenuItem>
          </Select>
        </FormControl>
      )}

      {sourceRecordName !== "CONSTANT" && (
        <FormControl
          variant="outlined"
          className="width-100"
          size="small"
          style={{ marginTop: "15px" }}>
          <Autocomplete
            freeSolo
            options={[" "]}
            getOptionLabel={(option) => {
              if (option === " ") return t("SPACE");
              return option;
            }}
            value={fillCharacter}
            onChange={(e, newValue) => {
              const valueToSave = newValue || " ";
              if (valueToSave !== fillCharacter) {
                setFillCharacter(valueToSave);
                saveToTreeNode("FILLCHAR", valueToSave);
              }
            }}
            onInputChange={(event, newInputValue) => {
              const valueToSave = newInputValue || " ";
              if (valueToSave !== fillCharacter) {
                setFillCharacter(valueToSave);
                saveToTreeNode("FILLCHAR", valueToSave);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("LBL_FILL_CHAR")}
                variant="outlined"
                size="small"
              />
            )}
          />
        </FormControl>
      )}

      {sourceRecordName !== "CONSTANT" && (
        <FormControl
          variant="outlined"
          size="small"
          style={{ marginTop: "15px" }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={trimDataChecked}
                onChange={handleTrimDataChange}
                name="trimData"
                color="primary"
              />
            }
            label={t("LBL_TRIM_DATA")}
          />
        </FormControl>
      )}

      <AdvancedProperties
        selectedNode={treeSelectedNode}
        isTradingPartnerLookup={true}
        updatedSelectedNodeWithAdvanceProperties={(val) => {
          if (val && val.hasOwnProperty("CHANGED")) delete val.CHANGED;
          // Handle empty string values by removing those properties
          const cleanedVal = Object.entries(val).reduce((acc, [key, value]) => {
            if (value !== "" && value !== null && value !== "--Select--") {
              acc[key] = value;
            }
            return acc;
          }, {});
          // Start with a clean copy of the current node
          const newNode = { ...treeSelectedNode };

          // Update or remove each advanced property
          Object.keys(val).forEach((key) => {
            if (
              val[key] === "" ||
              val[key] === null ||
              val[key] === "--Select--"
            ) {
              delete newNode[key];
            } else {
              newNode[key] = cleanedVal[key];
            }
          });

          // Add FIELDTYPE = 'ALPHA' if any advanced property is set
          if (Object.keys(cleanedVal).length > 0) {
            newNode.FIELDTYPE = "ALPHA";
          } else {
            // Remove FIELDTYPE if no advanced properties are set
            delete newNode.FIELDTYPE;
          }

          setTreeSelectedNode(newNode);
          setTreeData((prevData) =>
            prevData.map((node) =>
              node.key === treeSelectedNode.key ? newNode : node
            )
          );
        }}
      />

      <WorkflowSessionModal1
        sourceType={"callingWorkflow"}
        openModal={workflowSessionModal}
        handleClose={() => setWorkflowSessionModal(false)}
        valueHandler={handleWorkflowSessionModalValue}
        selection={"single"}
      />

    </>
  );
}

const styles = {
  nameIDAdornment: {
    color: "#666666",
    fontSize: "9px",
    position: "absolute",
    right: "13px",
    top: "-6px",
    backgroundColor: "#FFF",
    zIndex: "2",
    padding: "0 5px",
    height: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  treeContainer: {
    height: '100%',
    width: '100%',
    border: '1px solid #999',
    borderTopLeftRadius: '0px',
    borderTopRightRadius: '0px',
    borderRadius: '4px',
    minHeight: "83px",
    maxHeight: "83px", 
    overflowY: "auto"
  },
  treeHeaderContainer: {
    paddingTop: '8px',
    paddingBottom: '2px',
    paddingLeft: '0px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  }
};




